import type { Animation } from '@ionic/core';
import type { EventEmitter } from '@stencil/core';

import { Component, Element, Event, Host, Method, Prop, State, h } from '@stencil/core';
import { alertCloseAnimation } from './animations/close';
import { alertOpenAnimation } from './animations/open';
import { t } from '../../i18n';

/**
 * @slot  - Slot to add content to the alert. It is positioned under the alert title.
 */
@Component({
  tag: 'sy-alert',
  styleUrl: 'scss/index.scss',
  shadow: true,
})
export class Alert {
  private syCollapseEl: HTMLSyCollapseElement | undefined;

  @Element() el: HTMLSyAlertElement;

  @State() collapsed = false;

  /**
   * Title of the alert.
   */
  @Prop() header: string;

  /**
   * If true, the alert can be dimissed by clicking in the close button.
   */
  @Prop({ reflect: true }) dismissible = false;

  /**
   * If true, the slot can be collapsed by clicking the arrow button.
   * This property takes precedence over the dismissible property when both are declared.
   */
  @Prop({ reflect: true }) collapsible = false;

  /**
   * Defines the color and the icon of the alert.
   */
  @Prop({ reflect: true }) type: 'error' | 'warning' | 'success' | 'info' = 'error';

  /**
   * Icon name (use stardard lib)
   */
  @Prop({ reflect: true }) icon: string;

  /**
   * Emitted after alert is closed.
   */
  @Event() syAlertDidClose: EventEmitter<any>;

  /**
   * Method to close the alert. It will remove the alert from DOM.
   */
  @Method()
  async dismiss(): Promise<void> {
    this.syAlertDidClose.emit();
    this.el.remove();
  }

  /**
   * Method to collapse the alert.
   */
  @Method()
  async toggleCollapse(): Promise<void> {
    if (!this.syCollapseEl) return;

    const collapse = this.syCollapseEl;
    const internalCollapsed = this.collapsed;

    this.collapsed = !this.collapsed;

    if (internalCollapsed) {
      await collapse.open();
    }

    if (!internalCollapsed) {
      await collapse.close();
    }
  }

  private openAnimation = (collapseElement: HTMLElement): Animation => {
    return alertOpenAnimation(collapseElement);
  };

  private closeAnimation = (collapseElement: HTMLElement): Animation => {
    return alertCloseAnimation(collapseElement);
  };

  private typesIcons = {
    error: 'error',
    warning: 'warning',
    success: 'check_circle',
    info: 'info',
  };

  private showCollapse(): boolean {
    return this.collapsible && !!this.el.firstChild && !!this.header;
  }

  private renderHeader() {
    if (!this.header) {
      return (
        <div class="sy-alert-message">
          <slot></slot>
        </div>
      );
    }
    return <div class="sy-alert-title">{this.header}</div>;
  }

  private renderAction() {
    if (this.showCollapse()) {
      return (
        <sy-button
          accessibleLabel={this.collapsed ? t('EXPAND_ALERT', 'expand alert') : t('COLLAPSE_ALERT', 'collapse alert')}
          class="sy-button-collapse"
          content="icon-only"
          variant="clear"
          icon="keyboard_arrow_upward"
          small
          onClick={() => this.toggleCollapse()}
        ></sy-button>
      );
    }
    if (this.dismissible) {
      return (
        <sy-dismiss
          accessibleLabel={t('CLOSE_ALERT', 'close alert')}
          class="sy-alert-dismiss"
          onClick={() => this.dismiss()}
        ></sy-dismiss>
      );
    }
  }

  private renderContent() {
    if (!this.header) return;
    if (this.showCollapse()) {
      return (
        <sy-collapse
          openAnimation={this.openAnimation}
          closeAnimation={this.closeAnimation}
          opened
          class="sy-alert-message"
          ref={(el) => (this.syCollapseEl = el)}
        >
          <slot></slot>
        </sy-collapse>
      );
    }

    return (
      <div class="sy-alert-content">
        <slot></slot>
      </div>
    );
  }

  render() {
    return (
      <Host role="alert" class={{ collapsed: this.collapsed }}>
        <div class="sy-alert-wrapper">
          <div class="sy-alert-header">
            <div class="sy-alert-header-info">
              <div class="sy-alert-icon">
                <sy-icon name={this.icon || this.typesIcons[this.type]}></sy-icon>
              </div>
              {this.renderHeader()}
            </div>
            {this.renderAction()}
          </div>
          {this.renderContent()}
        </div>
      </Host>
    );
  }
}
